import type { AssetHistory, DoorEventSample, LaneInterval, LaneRowData, LaneSectionData, ReeferSample } from '../types'

/** Convert reefer state samples into lane intervals (raw DB values preserved) */
function buildStateIntervals(samples: ReeferSample[]): LaneInterval[] {
  if (!samples?.length) return []
  const intervals: LaneInterval[] = []
  for (let i = 0; i < samples.length; i++) {
    const start = new Date(samples[i].time).getTime()
    const end = i < samples.length - 1 ? new Date(samples[i + 1].time).getTime() : start + 60000
    intervals.push({ start, end, value: samples[i].value ?? '' })
  }
  return intervals
}

/** Convert door events into lane intervals */
function buildDoorIntervals(events: DoorEventSample[]): LaneInterval[] {
  if (!events?.length) return []
  const intervals: LaneInterval[] = []
  for (let i = 0; i < events.length; i++) {
    const start = new Date(events[i].time).getTime()
    const isLast = i === events.length - 1
    const end = isLast ? Date.now() : new Date(events[i + 1].time).getTime()
    const status = events[i].status
    const iv: LaneInterval = { start, end, value: status }
    // Custom tooltip for open-with-no-close
    if (isLast && status === 'open') {
      iv.tooltip = `Open Since ${new Date(start).toLocaleString()}`
    }
    intervals.push(iv)
  }
  return intervals
}

/** Build the Reefer lane section from asset history */
export function buildReeferSection(data: AssetHistory): LaneSectionData {
  const rows: LaneRowData[] = []

  const stateKeys: { key: string; label: string; colorMapKey: string; samples: ReeferSample[] }[] = [
    { key: 'powerState', label: 'Power State', colorMapKey: 'powerState', samples: data.powerState ?? [] },
    { key: 'powerMode', label: 'Power Mode', colorMapKey: 'powerMode', samples: data.powerMode ?? [] },
    { key: 'operation', label: 'Operation', colorMapKey: 'operation', samples: data.operation ?? [] },
    { key: 'powerSource', label: 'Power Source', colorMapKey: 'powerSource', samples: data.powerSource ?? [] },
    { key: 'engineSpeed', label: 'Engine Speed', colorMapKey: 'engineSpeed', samples: data.engineSpeed ?? [] },
    { key: 'airControl', label: 'Air Control', colorMapKey: 'airControl', samples: data.airControl ?? [] },
    { key: 'doorState', label: 'Door Status', colorMapKey: 'door', samples: data.doorState ?? [] },
  ]

  for (const { key, label, colorMapKey, samples } of stateKeys) {
    const intervals = buildStateIntervals(samples)
    if (intervals.length) {
      rows.push({ key, label, colorMapKey, intervals })
    }
  }

  return { title: 'Reefer', rows }
}

/** Build the Doors lane section */
export function buildDoorsSection(data: AssetHistory): LaneSectionData {
  const doorIntervals = buildDoorIntervals(data.doorEvents ?? [])
  const rows: LaneRowData[] = []
  if (doorIntervals.length) {
    rows.push({ key: 'door', label: 'Door', colorMapKey: 'door', intervals: doorIntervals })
  }
  return { title: 'Doors', rows }
}
